import { Theme } from "@mui/material";
import { CellClassParams, ColDef } from "ag-grid-community";
import { TimeframeTypeEnum } from "../../common/enums/TimeframeTypeEnum";
import { ITagValue } from "../../common/models/ITagValue";
import { getHighchartsLocalDateMilliseconds, getTimeframeTimeDisplayText } from "../../common/utilities/TimeframeUtilities";
import { RowDef } from "../../components/ag-grid-extensions/models/RowDef";
import { ITagValueCellRendererProps } from "../../components/ag-grid-extensions/renderers/TagValueRenderer";
import i18n from "../../services/localization-service/i18n";
import { ITagHistoryItem } from "../../services/tag-data-service/Models";
import { setPointEventTypeText } from "../../common/enums/SetPointEventTypeEnum";
import GraphUtils from "../../common/utilities/GraphUtils";
import { ExportingOptions, Options, OptionsStepValue } from "highcharts";
import Enumerable from "linq";
import { getHighchartsTooltipFormat, getSetpointPlotlines } from "../../common/utilities/ChartHelpers";
import { IDateRange } from "../../common/models/IDateRange";
import { IListTextTagMapping_V2 } from "../../common/models/ILookupList";
import { GraphSeriesOptionsType } from "../../services/trending-data-service/Models";
import { themifyHighchartsOptions } from "../../common/utilities/ThemifyHighchartsOptions";

const TAG_HISTORY_DEFAULT_COLOR: string = "#1E90FF";

const chartExportHeight: number = 768;
const chartExportWidth: number = 1024;
const exportScale: number = 1; // Defaults to 2


const createDesktopGridColumns = (theme: Theme, superUser: boolean): ColDef[] => {
    const cols = new Array<ColDef>({
        headerName: i18n.t("datetime.timestamp") as string,
        field: 'timestamp',
        sortable: true,
        sort: "desc",
        //flex: 1 => Bug 51745: Tag Values Grid Autosize Columns
        type: "timestamp"
    }, {
        headerName: i18n.t("tag.value") as string,
        field: "dataValue",
        sortable: true,
        cellRenderer: 'tagValueRenderer',
        cellRendererParams: {
            mobile: false,
            offline: false,
            manualEntry: false
        } as ITagValueCellRendererProps,
        //cellStyle: (params: ICellRendererParams) => {
        cellStyle: (params: CellClassParams) => {
            return {
                color: params?.data?.tag?.color ?? '',
                'fontWweight': params?.data?.tag?.color ? 'bold' : 'inherit',
                // SPR 2025-04-03 - Don't set lineHeight or padding for the cell, the grid will calculate it for us.
                // "lineHeight": `${theme.spacing(2)}`,
                // "padding": `${theme.spacing(1)}`
            }
        },
        //flex: 1 => Bug 51745: Tag Values Grid Autosize Columns
        type: "number"
    }, {
        headerName: i18n.t("uom.units") as string,
        field: "unit",
        sortable: false,
        filter: false
        //flex: 1 => Bug 51745: Tag Values Grid Autosize Columns
    }, {
        headerName: i18n.t("tag.source") as string,
        field: "source",
        sortable: true,
        //flex: 1 => Bug 51745: Tag Values Grid Autosize Columns
        type: "text"
    });

    if (superUser) {
        cols.push({
            headerName: i18n.t("datetime.acquisition"),
            field: 'acquisitionTimestamp',
            sortable: true,
            //flex: 1 => Bug 51745: Tag Values Grid Autosize Columns
            type: "timestamp"
        }, {
            headerName: i18n.t("datetime.modified"),
            field: 'modifiedTimestamp',
            sortable: true,
            //flex: 1 => Bug 51745: Tag Values Grid Autosize Columns
            type: "timestamp"
        }, {
            headerName: i18n.t("tag.rawValue"),
            field: 'rawValue',
            sortable: true,
            //flex: 1 => Bug 51745: Tag Values Grid Autosize Columns
            type: "number"
        })
    }

    return cols;
};

const createMobileGridColumns = (): ColDef[] => {

    const cols = new Array<ColDef>({
        headerName: i18n.t('tag.value') as string,
        field: "dataValue",
        sortable: false,
        autoHeight: true,
        // Bug 48502: React Tag Details Sort Issue
        // sortable: true,
        // sort: "desc",
        // comparator: numberComparator,

        //cellStyle: (params: ICellRendererParams) => {
        cellStyle: (params: CellClassParams) => {
            return {
                color: params?.data?.tag?.color ?? '',
                'fontWeight': params?.data?.tag?.color ? 'bold' : 'inherit',
                // SPR 2025-04-08 - Not sure why I still need to add this here because
                // the multiline column type should already do this.
                lineHeight: "normal",
                paddingTop: "var(--ag-spacing)",
                paddingBottom: "var(--ag-spacing)",
            };
        },
        // flex: 1, => Bug 51745: Tag Values Grid Autosize Columns
        cellRenderer: 'tagValueRenderer',
        cellRendererParams: {
            mobile: true,
            showTimestamp: true,
            offline: false,
            manualEntry: false
        } as ITagValueCellRendererProps,
        editable: false,
        type: "number"
    }, {
        headerName: i18n.t("tag.source") as string,
        field: "source",
        sortable: false,
        //sortable: true,
        // flex: 1 => Bug 51745: Tag Values Grid Autosize Columns
        type: "text"
    });

    return cols;
};

const projectRow = (
    tagValue: ITagValue): RowDef => {

    const row: RowDef = {
    };


    row['timestamp'] = getTimeframeTimeDisplayText(tagValue.displayTimeframe as TimeframeTypeEnum, new Date(tagValue?.timestamp as string));
    row['value'] = tagValue.displayText;
    row['dataValue'] = tagValue.value;
    row['unit'] = tagValue.unitDisplayName;
    row['source'] = setPointEventTypeText(tagValue.setPointEventType);
    row['acquisitionTimestamp'] = getTimeframeTimeDisplayText(TimeframeTypeEnum.None, new Date(tagValue?.createdTimestamp as string));
    row['modifiedTimestamp'] = getTimeframeTimeDisplayText(TimeframeTypeEnum.None, new Date(tagValue?.modifiedTimestamp as string));
    row['rawValue'] = tagValue.rawValue as number;
    row["rowID"] = `${tagValue.timestamp}:${tagValue.value}:${tagValue.setPointEventType}`;

    return row;
}


function getChartExportingOptions(name: string) {
    const exportingOptions: ExportingOptions =
    {
        buttons: undefined,
        scale: exportScale,
        sourceHeight: chartExportHeight,
        sourceWidth: chartExportWidth,
        fallbackToExportServer: false,
        filename: name,
        chartOptions: {
            chart: {
                // Highcharts stopped exporting a transparent background as white
                backgroundColor: "white"
            },
            subtitle: undefined
        }
    };

    return exportingOptions;
}


const TagHistoryUtils = {

    createGridRows: function (tagHistoryItem: ITagHistoryItem | undefined): Array<RowDef> {

        const rows = tagHistoryItem?.values?.map((o) => {

            return projectRow(o);

        }) ?? new Array<RowDef>();


        return rows;
    },
    createGridColumns: function (theme: Theme, isDesktop: boolean, superUser: boolean): ColDef[] {

        if (isDesktop) {
            return createDesktopGridColumns(theme, superUser);
        } else {
            return createMobileGridColumns();
        }
    },

    getChartOptions(
        theme: Theme,
        tagName: string,
        tagUnit: string,
        tagHistoryItem: ITagHistoryItem,
        listTextTagMapping: IListTextTagMapping_V2 | undefined,
        noDataMessage: string,
        showSetpointThresholds: boolean,
        isMobile: boolean,
        noTitle?: boolean,
        noSubtitle?: boolean,
        noYAxisTitle?: boolean,
        overrideYMin?: number,
        overrideYMax?: number
    ): Options | undefined {

        if (tagHistoryItem) {

            //Build data source for chart. You can't just hand the rows
            //in the data parameter to the chart.
            //.orderBy(v => new Date(v.timestamp).getTime())
            const tagTrendingData = Enumerable.from(tagHistoryItem.values)
                .orderBy(v => v.timestamp)
                .select(function (x: ITagValue) {
                    return [getHighchartsLocalDateMilliseconds(tagHistoryItem.displayTimeframe as TimeframeTypeEnum, x.timestamp as string), x.roundedValue ?? x.value];
                }).toArray();

            const trendColor: string = tagHistoryItem && tagHistoryItem.trendColor &&
                tagHistoryItem.trendColor.length > 0 ? tagHistoryItem.trendColor : TAG_HISTORY_DEFAULT_COLOR;

            const forceYAxis = overrideYMin !== undefined && overrideYMax !== undefined;
            let minThreshold = 0;
            let maxThreshold = 0;

            if (showSetpointThresholds &&  tagHistoryItem.setpoints.length) {
                minThreshold = Enumerable.from(tagHistoryItem.setpoints).min(o => o.value!);
                maxThreshold = Enumerable.from(tagHistoryItem.setpoints).max(o => o.value!);

                const minSeriesValue = tagTrendingData.length ? 
                                        Enumerable.from(tagTrendingData).select(o => o.at(1)).where(o => o !== undefined).min() :
                                        0;
                const maxSeriesValue = tagTrendingData.length ? 
                                        Enumerable.from(tagTrendingData).select(o => o.at(1)).where(o => o !== undefined).max() : 
                                        0;

                minThreshold = Math.min(minThreshold, minSeriesValue);
                maxThreshold = Math.max(maxThreshold, maxSeriesValue);
            }

            const highChartsOptions: Options =
            {
                chart: {
                    animation: false,
                    type: "line",
                    backgroundColor: "transparent",
                    style: {
                        font: "inherit"
                    },
                    //zoomType: 'xy',
                    zooming: {
                        type: 'xy'
                    },
                    reflow: true,
                    //height: '100%'
                    // TODO I am not sure how I should handle this since the view model should not know about the DOM -- Dan Lumley 2/25/2015
                    //renderTo: this.chartContainer

                },
                credits: {
                    enabled: false
                },
                exporting: getChartExportingOptions(tagName),
                lang: {
                    noData: noDataMessage
                },
                legend: {
                    enabled: false
                },
                series: [{
                    animation: false,
                    color: trendColor,
                    
                    data: tagTrendingData ? tagTrendingData : undefined,
                    //name: tagName + tagUnit ? " (" + this._tagUnits + ")" : ""),
                    name: tagUnit ? `${tagName} (${tagUnit})` : tagName,
                    //lineWidth: 1,
                    lineWidth: 2,
                    marker: {
                        enabled: false //, radius: 3
                    },
                    states: { hover: { lineWidth: 1 } },
                    //step: data.trendLineStep && data.trendLineStep !== "false" ? data.trendLineStep : false,
                    step: tagHistoryItem.trendLineStep && tagHistoryItem.trendLineStep !== 'false' ?
                        tagHistoryItem.trendLineStep as OptionsStepValue : undefined,
                    tagID: tagHistoryItem.tagID,
                    tooltip:
                    {
                        pointFormatter: GraphUtils.getSeriesTooltipPointFormatter(tagHistoryItem.tagID, listTextTagMapping)
                    },  
                    type: "line",
                    yAxis: 0.0
                } as GraphSeriesOptionsType
                ],
                title: (isMobile || noTitle) ?
                    undefined :
                    {
                        style: {
                            fontFamily: "inherit",
                            fontSize: "150%",
                            color: "#36f"
                        },
                        text: tagName
                    },
                subtitle: (isMobile || noSubtitle) ? undefined : {
                    text: !("ontouchstart" in document.documentElement) ? // Duane had document.ontouchstart === undefined
                        "Click and drag in the plot area to zoom in" :
                        "Pinch the chart to zoom in"
                },
                tooltip: {
                    valueDecimals: tagHistoryItem.decimalPlaces !== undefined && tagHistoryItem.decimalPlaces !== null ? tagHistoryItem.decimalPlaces : 2,
                    headerFormat: "<b>{point.key}</b><br/>",
                    xDateFormat: getHighchartsTooltipFormat(tagHistoryItem.displayTimeframe as TimeframeTypeEnum),  // "%m/%d/%Y %l:%M:%S %p",
                    style: {
                        fontSize: "inherit"
                    }
                },
                xAxis:
                    [
                        {
                            gridLineWidth: 0,
                            minorGridLineWidth: 0,
                            maxPadding: 0,
                            minPadding: 0,
                            lineWidth: 1,
                            tickWidth: 1,
                            tickLength: 7,
                            //minorTickInterval: "auto",
                            //minorTickWidth: 1,
                            //minorTickLength: 3,
                            // Do we need this. Ken did not have it.
                            dateTimeLabelFormats: {
                                second: "%m/%d/%Y", minute: "%m/%d/%Y", hour: "%m/%d/%Y", day: "%m/%d/%Y", week: "%m/%d/%Y", month: "%m/%d/%Y", year: "%m/%d/%Y"
                            },
                            type: "datetime",
                            labels: {
                                align: "center",
                                staggerLines: 2,
                                overflow: "justify",
                                x: 0.0,
                                y: 20.0,
                                style: {
                                    //color: this._defaultTextColor
                                }
                                // Do we need this? Ken had it.
                                //formatter: function () {
                                //return kendo.toString(new Date(this.value), "d")
                            }//,
                            //title: {
                            // style: {
                            //color: this._defaultTextColor
                            //}
                            //}
                        }
                    ],
                yAxis: [{
                    //gridLineWidth: 0,
                    min:  forceYAxis && overrideYMin !== undefined ? overrideYMin : showSetpointThresholds && minThreshold !== maxThreshold ? minThreshold : null,
                    max:  forceYAxis && overrideYMax !== undefined ? overrideYMax : showSetpointThresholds && minThreshold !== maxThreshold ? maxThreshold : null,
                    //minorGridLineWidth: 0,
                    //startOnTick: false,
                    minPadding: 0.0,
                    //endOnTick: false,
                    maxPadding: 0.01,
                    //lineWidth: 1,
                    ///tickWidth: 1,
                    ///tickLength: 5,
                    //minorTickInterval: "auto",
                    //minorTickWidth: 1,
                    //minorTickLength: 3,
                    //labels: { enabled: false },
                    //labels: {
                    //overflow: 'justify',
                    //style: {
                    //color: this._defaultTextColor
                    //}
                    //},
                    //tickColor: "blue",
                    plotLines: showSetpointThresholds ? getSetpointPlotlines(tagHistoryItem.setpoints) : undefined,
                    title: {
                        text: noYAxisTitle ? '' : tagUnit ? `${tagName} (${tagUnit})` : tagName
                        //, style: {
                        //color: this._defaultTextColor
                        //}
                    },
                    type: "linear"
                }]
            };

            //tagTrendingData = null;
            return themifyHighchartsOptions(highChartsOptions, theme);
        }
    },
    getGridNoDataMessage(tagName: string, dateRange?: IDateRange): string {

        let message = '';

        if (dateRange && dateRange.startDate && dateRange.endDate) {
            message = i18n.t("noData.noDataForObjectWithinDateRange", {
                startDate: dateRange.startDate.toLocaleString(),
                endDate: dateRange.endDate.toLocaleString(),
                objectName: tagName
            });

        } else {
            message = i18n.t("noData.noDataForObject", { objectName: tagName });
        }

        return message;
    },
    getChartNoDataMessage(tagName: string, dataLength: number, dateRange: IDateRange | undefined): string {

        let message = '';

        if (dataLength === 1) {
            if (dateRange && dateRange.startDate && dateRange.endDate) {
                message = i18n.t("noData.noDataForChartObjectWithinDateRange", {
                    startDate: dateRange.startDate.toLocaleString(),
                    endDate: dateRange.endDate.toLocaleString(),
                    objectName: tagName
                });
            } else {
                message = i18n.t("noData.noDataForChartObject", { objectName: tagName });
            }
        } else if (dataLength === 0) {
            if (dateRange && dateRange.startDate && dateRange.endDate) {
                message = i18n.t("noData.noDataForObjectWithinDateRange", {
                    startDate: dateRange.startDate.toLocaleString(),
                    endDate: dateRange.endDate.toLocaleString(),
                    objectName: tagName
                });
            } else {
                message = i18n.t("noData.noDataForObject", { objectName: tagName });
            }
        }

        return message;
    }
};

export default TagHistoryUtils;
